/**
 * Fleet-operations thresholds, served by the backend.
 *
 * These are *policy*, not presentation: the same numbers decide a sensor's status
 * server-side and its colour client-side, so they are fetched once from
 * `/meta/config` and hydrated into this module rather than being restated here.
 * The literals below are only a boot-time fallback for the window between first
 * paint and the config response (and for unit tests), and they mirror the
 * defaults in `dashboard_api/core/thresholds.py`.
 */

export interface SensorThresholds {
  battery_low: number;
  battery_critical: number;
  active_hours: number;
  expected_readings_per_day: number;
  reading_interval_hours: number;
  data_quality_good: number;
  data_quality_warning: number;
  flow_threshold_m3: number;
  null_streak_days: number;
  uptime_active_frac: number;
  flow_days_active_frac: number;
  // Analysis windows, in days — `dashboard_api/core/thresholds.py` "Analysis
  // windows" is the one definition; nothing in the SPA restates these numbers.
  /** Default for every scope-driven page and every un-parameterised fetch. */
  default_analysis_days: number;
  /** Widest window the API accepts; the "all" preset resolves to it. */
  max_analysis_days: number;
  /** Trailing week a single complete day is compared against. */
  baseline_days: number;
  /** Header presets — also the windows the anomaly cache is precomputed for. */
  window_presets_days: number[];
  /** Anomaly views' window: the widest precomputed one. */
  anomaly_window_days: number;
  /** Widest window a per-meter health grade is computed over. */
  sensor_health_max_days: number;
  /** Widest window the customer-facing analytics accept. */
  customer_max_days: number;
  /** The per-house day heatmap's fixed window. */
  heatmap_days: number;
  /** Fewest days a correlation is computed over. */
  min_correlation_days: number;
  /** Upper edges (exclusive) of NRW grades A..E in NRW %; F is at or above the last. */
  nrw_grade_edges: number[];
  forecast_days: number;
  /** Horizons the Analytics forecast panel offers, shortest first. */
  forecast_horizons_days: number[];
}

const FALLBACK: SensorThresholds = {
  battery_low: 20,
  battery_critical: 10,
  active_hours: 48,
  expected_readings_per_day: 4,
  reading_interval_hours: 6,
  data_quality_good: 75,
  data_quality_warning: 55,
  flow_threshold_m3: 0.04,
  null_streak_days: 3,
  uptime_active_frac: 0.6,
  flow_days_active_frac: 0.3,
  default_analysis_days: 30,
  max_analysis_days: 400,
  baseline_days: 7,
  window_presets_days: [7, 30, 60, 90],
  anomaly_window_days: 90,
  sensor_health_max_days: 90,
  customer_max_days: 90,
  heatmap_days: 30,
  min_correlation_days: 7,
  nrw_grade_edges: [15, 25, 35, 45, 60],
  forecast_days: 3,
  forecast_horizons_days: [3, 7, 14],
};

let current: SensorThresholds = FALLBACK;

/** Called once by the auth/config bootstrap with the server's values. */
export function hydrateThresholds(next: Partial<SensorThresholds> | undefined): void {
  if (next) current = { ...FALLBACK, ...next };
}

/** The active thresholds. Read at call time — never destructure at module scope. */
export function thresholds(): SensorThresholds {
  return current;
}
